import React, { useEffect, useMemo, useState } from 'react';
import {
    Box,
    Button,
    Checkbox,
    Chip,
    FormControl,
    InputLabel,
    ListItemText,
    ListSubheader,
    MenuItem,
    Select,
    SelectChangeEvent,
} from '@mui/material';
import FilterAltOffIcon from '@mui/icons-material/FilterAltOff';
import {
    IFilterOption,
    IStoryViewerFilters,
    ITagCategory,
    ITagItem,
    STORY_OCCASIONS,
    STORY_PRODUCTS,
    STORY_RECIPIENTS,
} from '../../lib/storyViewer';
import { hasActiveFilters } from './useStoryViewerData';

interface IProps {
    filters: IStoryViewerFilters;
    tags: ITagItem[];
    categories: ITagCategory[];
    loading: boolean;
    onChange: (patch: Partial<IStoryViewerFilters>) => void;
    onClear: () => void;
}

const SELECT_SX = { minWidth: 180, flex: '1 1 180px', maxWidth: 260 };

const SingleSelect = ({
    label, name, value, options, loading, onChange,
}: {
    label: string;
    name: keyof IStoryViewerFilters;
    value: string;
    options: IFilterOption[];
    loading: boolean;
    onChange: (patch: Partial<IStoryViewerFilters>) => void;
}) => (
    <FormControl size="small" sx={SELECT_SX}>
        <InputLabel id={`story-viewer-${name}-label`}>{label}</InputLabel>
        <Select
            labelId={`story-viewer-${name}-label`}
            id={`story-viewer-${name}`}
            value={value}
            label={label}
            disabled={loading}
            onChange={(event: SelectChangeEvent<string>) => onChange({ [name]: event.target.value } as Partial<IStoryViewerFilters>)}
        >
            <MenuItem value=""><em>All</em></MenuItem>
            {options.map((option) => (
                <MenuItem key={`${option.value}-${option.title}`} value={String(option.value)}>{option.title}</MenuItem>
            ))}
        </Select>
    </FormControl>
);

const StoryViewerFilters = ({ filters, tags, categories, loading, onChange, onClear }: IProps) => {
    const [pendingTags, setPendingTags] = useState<number[]>(filters.tags);

    useEffect(() => { setPendingTags(filters.tags); }, [filters.tags]);

    const tagsById = useMemo(() => {
        const map = new Map<number, ITagItem>();
        tags.forEach((tag) => map.set(tag.id, tag));
        return map;
    }, [tags]);

    const groupedTags = useMemo(() => {
        const byCategory = new Map<number, ITagItem[]>();
        tags.forEach((tag) => {
            const list = byCategory.get(tag.categoryId) || [];
            list.push(tag);
            byCategory.set(tag.categoryId, list);
        });
        const orderedCategoryIds = categories.map((c) => c.id);
        byCategory.forEach((_list, id) => { if (!orderedCategoryIds.includes(id)) orderedCategoryIds.push(id); });
        return orderedCategoryIds
            .filter((id) => (byCategory.get(id) || []).length > 0)
            .map((id) => ({
                id,
                name: categories.find((c) => c.id === id)?.name || byCategory.get(id)?.[0]?.categoryName || 'Other',
                tags: [...(byCategory.get(id) || [])].sort((a, b) => a.name.localeCompare(b.name)),
            }));
    }, [tags, categories]);

    const sameTags = (a: number[], b: number[]) => a.length === b.length && a.every((v) => b.includes(v));

    const applyTags = (next: number[]) => {
        if (!sameTags(next, filters.tags)) onChange({ tags: next });
    };

    const handleTagsChange = (event: SelectChangeEvent<number[]>) => {
        const value = event.target.value;
        const next = (typeof value === 'string' ? value.split(',').map(Number) : value).filter((n) => Number.isFinite(n));
        setPendingTags(next);
    };

    const removeTag = (tagId: number) => {
        const next = filters.tags.filter((id) => id !== tagId);
        setPendingTags(next);
        onChange({ tags: next });
    };

    const active = hasActiveFilters(filters);

    return (
        <Box>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, alignItems: 'center' }}>
                <SingleSelect label="Occasion" name="occasionId" value={filters.occasionId} options={STORY_OCCASIONS} loading={loading} onChange={onChange} />
                <SingleSelect label="Recipient" name="recipientId" value={filters.recipientId} options={STORY_RECIPIENTS} loading={loading} onChange={onChange} />
                <SingleSelect label="Product" name="productId" value={filters.productId} options={STORY_PRODUCTS} loading={loading} onChange={onChange} />
                <FormControl size="small" sx={{ ...SELECT_SX, maxWidth: 320 }}>
                    <InputLabel id="story-viewer-tags-label">Tags</InputLabel>
                    <Select
                        labelId="story-viewer-tags-label"
                        id="story-viewer-tags"
                        multiple
                        value={pendingTags}
                        label="Tags"
                        disabled={loading || tags.length === 0}
                        onChange={handleTagsChange}
                        onClose={() => applyTags(pendingTags)}
                        renderValue={(selected) =>
                            selected.length === 0
                                ? ''
                                : selected.map((id) => tagsById.get(id)?.name || id).join(', ')
                        }
                        MenuProps={{ PaperProps: { sx: { maxHeight: 420 } } }}
                    >
                        {groupedTags.flatMap((group) => [
                            <ListSubheader key={`cat-${group.id}`} sx={{ fontWeight: 600, lineHeight: '32px' }}>{group.name}</ListSubheader>,
                            ...group.tags.map((tag) => (
                                <MenuItem key={tag.id} value={tag.id} dense>
                                    <Checkbox size="small" checked={pendingTags.includes(tag.id)} sx={{ p: 0.5, mr: 1 }} />
                                    <ListItemText primary={tag.name} />
                                </MenuItem>
                            )),
                        ])}
                    </Select>
                </FormControl>
                {active && (
                    <Button
                        variant="outlined"
                        size="small"
                        startIcon={<FilterAltOffIcon />}
                        onClick={onClear}
                        disabled={loading}
                        sx={{ height: 40 }}
                    >
                        Clear
                    </Button>
                )}
            </Box>
            {filters.tags.length > 0 && (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mt: 1.25 }}>
                    {filters.tags.map((id) => {
                        const tag = tagsById.get(id);
                        return (
                            <Chip
                                key={id}
                                size="small"
                                color="primary"
                                variant="outlined"
                                label={tag ? `${tag.categoryName}: ${tag.name}` : `#${id}`}
                                onDelete={() => removeTag(id)}
                                disabled={loading}
                            />
                        );
                    })}
                </Box>
            )}
        </Box>
    );
};

export default StoryViewerFilters;
